import {Message} from "react-hook-form/dist/types/errors";
import {Validate, ValidationRule} from "react-hook-form/dist/types/validator";
import {FieldPathValue} from "react-hook-form";

declare type RegisterOptions = Partial<{
  required: Message | ValidationRule<boolean>;
  min: ValidationRule<number | string>;
  max: ValidationRule<number | string>;
  maxLength: ValidationRule<number | string>;
  minLength: ValidationRule<number | string>;
  pattern: ValidationRule<RegExp>;
  validate: Record<string, Validate<FieldPathValue<any, any>>>;
  valueAsNumber: boolean;
  valueAsDate: boolean;
  setValueAs: (value: any) => any;
  shouldUnregister?: boolean;
}>;

// eslint-disable-next-line no-shadow
export enum FormValidationType {
  REQUIRED = 1,
  REQUIRED_IF = 2,
  MAX_STRING_LENGTH = 3,
  CONFIRMED = 4,
  PASSWORD = 5,
  EMAIL = 6,
  ZIP = 7,
  URL = 8,
}

const messages = {
  [FormValidationType.REQUIRED]: ':fieldNameは必須です',
  [FormValidationType.MAX_STRING_LENGTH]: ':fieldNameは:value文字以下で指定してください',
  [FormValidationType.REQUIRED_IF]: ':fieldNameは必須です',
  [FormValidationType.CONFIRMED]: ':otherFieldNameと一致しません',
  [FormValidationType.PASSWORD]: ':fieldNameには半角の英小文字、英大文字、数字をそれぞれ1種類以上含む8文字以上の文字列を指定してください',
  [FormValidationType.EMAIL]: '有効なメールアドレスを指定してください',
  [FormValidationType.ZIP]: '有効な郵便番号（ハイフンなし）を指定してください',
  [FormValidationType.URL]: '有効なURLを指定してください',
}

interface Validation {
  type: FormValidationType,
  value?: any,
  otherFieldName?: string,
  otherFieldValue?: string | number | boolean,
  typeName?: string,
  conditionFunc?: () => boolean,
}

const createRequiredOption = (fieldName: string, validation: Validation): Message => {
  return messages[validation.type].replace(':fieldName', fieldName);
}

const createMaxStringLengthOption = (fieldName: string, validation: Validation): ValidationRule<string> => {
  if (validation.value === undefined) {
    throw new Error('valueを指定してください。');
  }

  return {
    value: validation.value,
    message: messages[validation.type]
      .replace(':fieldName', fieldName)
      .replace(':value',validation. value)
  };
}

const isNotEmpty = (value: any): boolean => {
  return (value !== '') && (value !== null) && (value !== undefined)
}

const createRequiredIfOption = (fieldName: string, validation: Validation) => {
  return (value: any) => {
    if (validation?.conditionFunc === undefined) {
      throw new Error('conditionFuncを指定してください。');
    }

    return (!validation.conditionFunc() || isNotEmpty(value))
      || messages[validation.type].replace(':fieldName', fieldName);
  }
}

const createConfirmedOption = (validation: Validation) => {
  if (validation?.otherFieldName === undefined) {
    throw new Error('otherFieldNameを指定してください。');
  }

  return (value: any) => value === validation.otherFieldValue
    || messages[validation.type].replace(':otherFieldName', validation.otherFieldName ?? '');
}

const createPasswordOption = (fieldName: string, validation: Validation) => {
  return (value: string) => {
    return (!isNotEmpty(value) || /^(?=.*?[a-z])(?=.*?[A-Z])(?=.*?\d)[a-zA-Z\d]{8,64}$/.test(value))
      || messages[validation.type].replace(':fieldName', fieldName);

  }
}

const createEmailOption = (validation: Validation) => {
  return (value: string) => {
    return (!isNotEmpty(value) || /^[a-z\d!#$%&'*+\-/=?^_`{|}~][a-z\d!#$%&'*+\-/=?^_`{|}~.]{0,63}@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value))
      || messages[validation.type];
  }
}

const createZipOption = (validation: Validation) => {
  return (value: string) => {
    return (!isNotEmpty(value) || /^[0-9]{7}$/i.test(value))
      || messages[validation.type];
  }
}

const createUrlOption = (validation: Validation) => {
  return (value: string) => {
    return (!isNotEmpty(value) || /https?:\/\/[-_.!~*'()a-zA-Z0-9;/?:@&=+$,%#\u3000-\u30FE\u4E00-\u9FA0\uFF01-\uFFE3]+/g.test(value))
      || messages[validation.type];
  }
}

export const makeRegisterOptions = (fieldName: string, validations: Validation[]): RegisterOptions => {
  const registerOptions: RegisterOptions = {};

  validations.forEach((validation) => {
    switch (validation.type) {
      case FormValidationType.REQUIRED:
        registerOptions.required = createRequiredOption(fieldName, validation);
        break;

      case FormValidationType.MAX_STRING_LENGTH:
        registerOptions.maxLength = createMaxStringLengthOption(fieldName, validation);
        break;

      case FormValidationType.REQUIRED_IF:
        if (registerOptions.validate === undefined) {
          registerOptions.validate = {}
        }
        if (validation?.typeName === undefined) {
          throw new Error('typeNameを指定してください。');
        }
        registerOptions.validate[validation.typeName] = createRequiredIfOption(fieldName, validation);
        break;

      case FormValidationType.CONFIRMED:
        if (registerOptions.validate === undefined) {
          registerOptions.validate = {}
        }
        if (validation?.typeName === undefined) {
          throw new Error('typeNameを指定してください。');
        }
        registerOptions.validate[validation.typeName] = createConfirmedOption(validation);
        break;

      case FormValidationType.PASSWORD:
        if (registerOptions.validate === undefined) {
          registerOptions.validate = {}
        }
        if (validation.typeName === undefined) {
          throw new Error('typeNameを指定してください。');
        }
        registerOptions.validate[validation.typeName] = createPasswordOption(fieldName, validation);
        break;

      case FormValidationType.EMAIL:
        if (registerOptions.validate === undefined) {
          registerOptions.validate = {}
        }
        if (validation.typeName === undefined) {
          throw new Error('typeNameを指定してください。');
        }
        registerOptions.validate[validation.typeName] = createEmailOption(validation);
        break;

      case FormValidationType.ZIP:
        if (registerOptions.validate === undefined) {
          registerOptions.validate = {}
        }
        if (validation.typeName === undefined) {
          throw new Error('typeNameを指定してください。');
        }
        registerOptions.validate[validation.typeName] = createZipOption(validation);
        break;

      case FormValidationType.URL:
        if (registerOptions.validate === undefined) {
          registerOptions.validate = {}
        }
        if (validation.typeName === undefined) {
          throw new Error('typeNameを指定してください。');
        }
        registerOptions.validate[validation.typeName] = createUrlOption(validation);
        break;

      default:
        throw new Error(`想定外のValidationType（${validation.type}）が渡されました。`);
    }

    return null;
  });

  return registerOptions;
}